import React, { useState, useEffect } from 'react';
import { RotateCcw } from 'lucide-react';
import { useAuthStore } from '../../stores/useAuthStore';
import { LoadingSpinner } from '../LoadingSpinner';

interface VerificationFormProps {
  onSuccess: () => void;
  email: string; // We'll pre-fill this
}

export const VerificationForm: React.FC<VerificationFormProps> = ({ onSuccess, email }) => {
  const { verifyClient, error, isValidating } = useAuthStore();
  const [code, setCode] = useState(['', '', '', '', '', '']);

  // Handle input change for individual digits
  const handleChange = (index: number, value: string) => {
    // Allow only numbers
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace to focus previous input
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanCode = code.join('');
    if (cleanCode.length !== 6) return;

    try {
      await verifyClient(email, cleanCode);
      onSuccess();
    } catch (err) {
      // Error handled in store
    }
  };

  // Check if code is complete to auto-submit? Prefer manual button for clarity or effect for now.

  return (
    <div className="space-y-6 animate-fade-in text-center">
      <div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Verifica tu correo</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
            Hemos enviado un código de 6 dígitos a <span className="font-semibold text-gray-700 dark:text-gray-200">{email}</span>.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex justify-center gap-2">
            {code.map((digit, index) => (
            <input
                key={index}
                id={`code-${index}`}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-14 text-center text-2xl font-bold border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all shadow-sm"
                disabled={isValidating}
            />
            ))}
        </div>

        {error && (
            <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm rounded-lg">
            {error}
            </div>
        )}

        <button
            type="submit"
            disabled={isValidating || code.join('').length !== 6}
            className="w-full bg-sky-500 hover:bg-sky-600 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-lg font-bold transition-all transform active:scale-[0.98] flex items-center justify-center shadow-lg shadow-sky-500/30"
        >
            {isValidating ? (
            <LoadingSpinner size="sm" message="" />
            ) : (
            'Verificar Cuenta'
            )}
        </button>
      </form>

      <div className="text-sm">
        <p className="text-gray-500 dark:text-gray-400 mb-1">¿No recibiste el código?</p>
        <button 
            type="button" 
            className="flex items-center justify-center gap-1 mx-auto text-sky-600 dark:text-sky-400 hover:underline font-medium"
            onClick={() => {/* Implement resend logic if backend supports it later */}}
        >
            <RotateCcw className="w-4 h-4" /> Reenviar código
        </button>
      </div>
    </div>
  );
};
